import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Layout, Input } from '../../../shared/components'
import authService from '../../../services/authService'
import { useAuth } from '../../../store/modules/auth/hooks'

interface ResetPasswordFormData {
  password: string
  confirmPassword: string
}

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { isAuthenticated, logout } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [isValidatingToken, setIsValidatingToken] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isTokenValid, setIsTokenValid] = useState(false)
  const token = searchParams.get('token')

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<ResetPasswordFormData>({
    defaultValues: {
      password: '',
      confirmPassword: ''
    }
  })

  const watchedPassword = watch('password')

  useEffect(() => {
    // Se houver token na URL e usuário estiver logado, fazer logout
    if (token && isAuthenticated) {
      logout()
    }
  }, [token, isAuthenticated, logout])

  useEffect(() => {
    if (!token) {
      setError('Token de recuperação não encontrado. Por favor, solicite um novo link.')
      setIsValidatingToken(false)
      return
    }

    // Validar token ao carregar a página
    const validateToken = async () => {
      setIsValidatingToken(true)
      setError('')
      
      try {
        const response = await authService.validateResetToken(token)
        if (response.success && response.valid) {
          setIsTokenValid(true)
        } else {
          setError(response.message || 'Token inválido ou expirado. Por favor, solicite um novo link.')
          setIsTokenValid(false)
        }
      } catch (error: any) {
        setError(error.message || 'Erro ao validar token. Por favor, solicite um novo link.')
        setIsTokenValid(false)
      } finally {
        setIsValidatingToken(false)
      }
    }

    validateToken()
  }, [token])

  const onSubmit = async (data: ResetPasswordFormData) => {
    setError('')

    if (!token) {
      setError('Token inválido')
      return
    }

    setIsLoading(true)

    try {
      const response = await authService.resetPassword(token, data.password)
      if (response.success) {
        setSuccess(true)
        setTimeout(() => {
          navigate('/login')
        }, 2000)
      } else {
        setError('Erro ao redefinir senha. Tente novamente.')
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao redefinir senha. O token pode ter expirado.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Layout>
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl w-full bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row">
          
          {/* Lado Esquerdo - Imagem Decorativa */}
          <div className="w-full md:w-1/2 bg-blue-600 relative hidden md:block">
            <img 
              src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop" 
              alt="BaterPower Redefinir Senha" 
              className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/90 to-blue-900/90"></div>
            
            <div className="relative z-10 flex flex-col items-center justify-center h-full text-white px-12 text-center p-12">
              <div className="mb-8 p-4 bg-white/10 rounded-full backdrop-blur-sm">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold mb-6 font-display">Crie uma nova senha</h2>
              <p className="text-blue-100 text-lg leading-relaxed">
                Escolha uma senha forte para garantir a segurança da sua conta.
              </p>
            </div>
          </div>

          {/* Lado Direito - Formulário */}
          <div className="w-full md:w-1/2 p-8 md:p-12 lg:p-16 flex flex-col justify-center">
            <div className="text-center md:text-left mb-10">
              <h2 className="text-3xl font-bold text-gray-900 font-display">Redefinir Senha</h2>
              <p className="mt-3 text-base text-gray-600">Digite sua nova senha abaixo.</p>
            </div>

            {/* Success Message */}
            {success && (
              <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-start shadow-sm">
                <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-medium">Senha redefinida com sucesso!</p>
                  <p className="text-sm mt-1">Redirecionando para o login...</p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center shadow-sm">
                <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            {/* Loading State */}
            {isValidatingToken && (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center">
                  <svg className="animate-spin h-8 w-8 text-blue-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-sm text-gray-600">Validando token...</p>
                </div>
              </div>
            )}

            {/* Form */}
            {!success && !isValidatingToken && token && isTokenValid && (
              <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Nova Senha
                  </label>
                  <Input
                    id="password"
                    type="password"
                    {...register('password', {
                      required: 'Senha é obrigatória',
                      minLength: { value: 8, message: 'A senha deve ter pelo menos 8 caracteres' }
                    })}
                    className={`w-full py-3 border rounded-xl focus:border-transparent transition-all bg-gray-50 focus:bg-white text-gray-900 placeholder-gray-400 ${
                      errors.password ? 'border-red-300' : 'border-gray-200'
                    }`}
                    placeholder="Digite sua nova senha"
                    error={!!errors.password}
                    helperText={errors.password?.message || 'Mínimo de 8 caracteres'}
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar Nova Senha
                  </label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    {...register('confirmPassword', {
                      required: 'Confirmação de senha é obrigatória',
                      validate: (value) => value === watchedPassword || 'As senhas não coincidem'
                    })}
                    className={`w-full py-3 border rounded-xl focus:border-transparent transition-all bg-gray-50 focus:bg-white text-gray-900 placeholder-gray-400 ${
                      errors.confirmPassword ? 'border-red-300' : 'border-gray-200'
                    }`}
                    placeholder="Confirme sua nova senha"
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg shadow-blue-500/30 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 hover:shadow-blue-600/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transition-all transform hover:-translate-y-0.5"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Redefinindo...
                    </div>
                  ) : (
                    'Redefinir Senha'
                  )}
                </button>

                <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                  <p className="text-sm text-gray-600">
                    Lembrou sua senha?{' '}
                    <Link to="/login" className="font-semibold text-blue-600 hover:text-blue-700 hover:underline transition-colors">
                      Voltar para o login
                    </Link>
                  </p>
                </div>
              </form>
            )}

            {!token && !success && !isValidatingToken && (
              <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Token de recuperação não encontrado.
                </p>
                <Link to="/esqueci-senha" className="font-semibold text-blue-600 hover:text-blue-700 hover:underline transition-colors">
                  Solicitar novo link de recuperação
                </Link>
              </div>
            )}

            {token && !isTokenValid && !success && !isValidatingToken && (
              <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <p className="text-sm text-gray-600 mb-4">
                  O link de recuperação é inválido ou expirou.
                </p>
                <Link to="/esqueci-senha" className="font-semibold text-blue-600 hover:text-blue-700 hover:underline transition-colors">
                  Solicitar novo link de recuperação
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default ResetPassword

